/**
 * ExpiredTrialBanner
 *
 * Premium, non-dismissible top banner displayed whenever the workspace is in
 * a read-only state (expired_trial or cancelled).
 *
 * Design: Calm Control brand — deep slate-indigo bg, white text, accent-blue CTA.
 * Intent: trustworthy, not aggressive. Similar to Linear's payment-failed banner.
 *
 * Rendered globally in AppShell so it appears on every admin page without
 * touching individual route components.
 */

import { Lock, CreditCard, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useWorkspaceAccess } from '../../hooks/useWorkspaceAccess'
import { Button } from '../ui/button'

export function ExpiredTrialBanner() {
  const { workspaceStatus, isLoading } = useWorkspaceAccess()
  const navigate = useNavigate()

  // Only show for expired/cancelled states
  if (isLoading || (workspaceStatus !== 'expired_trial' && workspaceStatus !== 'cancelled')) {
    return null
  }

  const isExpired   = workspaceStatus === 'expired_trial'
  const headline    = isExpired
    ? 'Your ODOnboard trial has ended.'
    : 'Your workspace has been cancelled.'
  const subline     = isExpired
    ? 'Your workspace is in read-only mode. Activate a plan to resume adding employees and launching onboardings. Your data is fully preserved.'
    : 'Your workspace is in read-only mode. Reactivate your plan to restore full access. Your data is fully preserved.'

  return (
    <div
      className="w-full bg-brand text-white"
      role="alert"
      aria-live="polite"
      data-testid="expired-trial-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">

          {/* Left: icon + message */}
          <div className="flex items-start gap-3 min-w-0">
            <div className="mt-0.5 shrink-0">
              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                <Lock className="w-3.5 h-3.5 text-white/80" />
              </div>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white leading-tight">
                {headline}
              </p>
              <p className="text-xs text-white/60 mt-0.5 leading-relaxed max-w-prose">
                {subline}
              </p>
            </div>
          </div>

          {/* Right: CTAs */}
          <div className="flex items-center gap-3 shrink-0">
            <Button
              size="sm"
              onClick={() => navigate('/settings?tab=billing')}
              className="bg-accent hover:bg-[#3B5CE8] text-white border-0 shadow-none text-xs font-medium h-8 px-4"
            >
              <CreditCard className="w-3.5 h-3.5 mr-1.5" />
              Activate plan
            </Button>
            <button
              type="button"
              onClick={() => navigate('/settings?tab=billing')}
              className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors"
            >
              View billing
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}
